import { AddItem, Box, Dropzone } from '@wix/design-system';
import React, { FC, PropsWithChildren, RefObject, useCallback, useEffect } from 'react';

import { AttachingMethod } from '../../../../types';
import { useAppDispatch } from '../../../common/store';
import { loadAttaches } from '../../../common/thunks/attaches.thunk';
import { useIsAttachmentAvailable, useIsSmallScreen } from '../../hooks';
import { attachmentsDropzoneDataHooks } from './AttachmentsDropzone.dataHooks';
import s from './styles.scss';

type AttachmentsDropzoneProps = PropsWithChildren<{
  chatbotRef: RefObject<HTMLDivElement>;
}>;

export const AttachmentsDropzone: FC<AttachmentsDropzoneProps> = ({ children, chatbotRef }) => {
  const dispatch = useAppDispatch();
  const isSmallScreen = useIsSmallScreen();
  const isAttachmentAvailable = useIsAttachmentAvailable();

  const onDrop = useCallback(
    (files: File[]) => {
      if (isAttachmentAvailable) {
        dispatch(loadAttaches({ files, attachingMethod: AttachingMethod.Drop }));
      }
    },
    [dispatch, isAttachmentAvailable],
  );

  useEffect(() => {
    if (isAttachmentAvailable || !chatbotRef.current) {
      // do not disable Dropzone if not needed
      return;
    }

    const chatbotElement = chatbotRef.current;
    const stopDragZoneEvents = (event: Event): void => event.stopPropagation();

    // stop these events to turn off Dropzone component if attachment is not allowed to upload
    chatbotElement.addEventListener('drop', stopDragZoneEvents);
    chatbotElement.addEventListener('dragenter', stopDragZoneEvents);
    chatbotElement.addEventListener('dragleave', stopDragZoneEvents);

    return () => {
      chatbotElement.removeEventListener('drop', stopDragZoneEvents);
      chatbotElement.removeEventListener('dragenter', stopDragZoneEvents);
      chatbotElement.removeEventListener('dragleave', stopDragZoneEvents);
    };
  }, [chatbotRef, isAttachmentAvailable]);

  return (
    <Dropzone className={s.dropzone} onDrop={onDrop} dataHook={attachmentsDropzoneDataHooks.dropzone()}>
      <Dropzone.Content>{children}</Dropzone.Content>
      <Dropzone.Overlay>
        <Box
          position="absolute"
          top={0}
          bottom={0}
          left={0}
          right={0}
          padding={isSmallScreen ? 'SP1' : 'SP2'}
          zIndex={1}
        >
          <AddItem theme="filled" size="medium" dataHook={attachmentsDropzoneDataHooks.addItem()}>
            Add your files
          </AddItem>
        </Box>
      </Dropzone.Overlay>
    </Dropzone>
  );
};
